
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header'; 
import Sidebar from './Sidebar';
import { useSupabaseClient } from '@/hooks/useSupabaseClient';
import { useToast } from '@/hooks/use-toast';

type DashboardLayoutProps = {
  children: React.ReactNode;
}

const EnhancedDashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userName, setUserName] = useState('Användare');
  const [userInitials, setUserInitials] = useState('SU');
  const [userTitle, setUserTitle] = useState<string | undefined>(undefined);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const supabase = useSupabaseClient();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          navigate('/auth');
          return;
        }
        
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          // Get user name from metadata
          const name = user.user_metadata?.name || user.email?.split('@')[0] || 'Användare';
          setUserName(name);
          
          // Get user title from metadata
          setUserTitle(user.user_metadata?.title);
          
          // Get avatar URL from metadata
          setAvatarUrl(user.user_metadata?.avatar_url || null);
          
          // Generate initials from name
          const nameParts = name.split(' ');
          if (nameParts.length > 1) {
            setUserInitials(`${nameParts[0][0]}${nameParts[1][0]}`);
          } else if (nameParts.length === 1) {
            setUserInitials(nameParts[0].substring(0, 2));
          }
        }
      } catch (error) {
        console.error('Session check error:', error);
        toast({
          title: "Sessionsfel",
          description: "Kunde inte verifiera din session. Logga in igen.",
          variant: "destructive",
        });
        navigate('/auth');
      } finally {
        setIsLoading(false);
      }
    };
    
    checkSession();
  }, [supabase, navigate, toast]);
  
  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/');
    } catch (error) {
      console.error('Sign out error:', error);
      toast({
        title: "Utloggningsfel",
        description: "Det gick inte att logga ut. Försök igen.",
        variant: "destructive",
      });
    }
  };
  
  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
    </div>;
  }
  
  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar 
        isSidebarOpen={isSidebarOpen} 
        setIsSidebarOpen={setIsSidebarOpen} 
        userName={userName}
        userInitials={userInitials}
        userTitle={userTitle}
        avatarUrl={avatarUrl}
        onSignOut={handleSignOut}
      />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header 
          setIsSidebarOpen={setIsSidebarOpen} 
          userName={userName}
        />
        
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50">
          {children}
        </main>
      </div>
    </div>
  );
};

export default EnhancedDashboardLayout;
